import React, { useEffect, useMemo, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { usePermissions } from "../hooks/usePermissions";
import { useReadOnly } from "../hooks/useReadOnly";
import { toast } from "sonner";
import {
  Plus, Trash2, FileText, Upload, Settings, Receipt, Download, FileSignature, AlertTriangle,
  ShieldCheck, PenLine, Send, RefreshCw, FileCode, Lock,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";
import CertificacionSII from "../components/CertificacionSII";
import { useAuth } from "../contexts/AuthContext";

const IVA_TASA = 0.19;
const EMPTY_ITEM = { nombre: "", cantidad: 1, precio_unitario: 0, descuento_pct: 0, exento: false };

const inputCls = "w-full px-3 py-2 border border-border rounded-sm text-sm bg-background";
const CARD = "bg-card border border-border rounded-md";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-60";
const BTN_GHOST = "inline-flex items-center gap-1 border border-border hover:bg-muted text-foreground rounded-sm px-2.5 py-1.5 text-[13px]";

function calcTotales(tipoCfg, items) {
  const afectoDoc = tipoCfg?.afecto !== false;
  let neto = 0, exento = 0;
  for (const it of items) {
    const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
    const monto = Math.round(bruto * (1 - (Number(it.descuento_pct) || 0) / 100));
    if (it.exento || !afectoDoc) exento += monto;
    else neto += monto;
  }
  const iva = Math.round(neto * IVA_TASA);
  return { neto, exento, iva, total: neto + exento + iva };
}

export default function Facturador() {
  const { canWrite } = usePermissions();
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const editable = canWrite("facturador");
  // Solo el superadmin de plataforma (sin organización) ve el asistente de certificación SII
  const canCertificacionSII = user?.role === "superadmin" && !user?.organizacion_id;
  const emitBlockedReason = readOnly
    ? "No disponible en la demo"
    : !editable
      ? "No tienes permiso para emitir documentos"
      : null;
  const [tab, setTab] = useState("emitir");
  const [tipos, setTipos] = useState([]);

  useEffect(() => {
    api.get("/facturador/tipos").then(({ data }) => setTipos(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "certificacion" && !canCertificacionSII) setTab("emitir");
  }, [tab, canCertificacionSII]);

  return (
    <Page>
      <PageHeader
        title="Facturador electrónico"
        subtitle="Emisión interna de documentos tributarios (DTE)"
        testid="facturador-header"
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5">
          <TabsTrigger value="emitir" data-testid="tab-emitir">
            <Receipt className="h-4 w-4 mr-1.5" /> Emitir
          </TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">
            <FileText className="h-4 w-4 mr-1.5" /> Documentos
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">
            <Settings className="h-4 w-4 mr-1.5" /> Configuración
          </TabsTrigger>
          {canCertificacionSII && (
            <TabsTrigger value="certificacion" data-testid="tab-certificacion">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Certificación SII
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="emitir">
          <EmitirTab tipos={tipos} blockedReason={emitBlockedReason} onEmitido={() => setTab("documentos")} />
        </TabsContent>
        <TabsContent value="documentos">
          <DocumentosTab />
        </TabsContent>
        <TabsContent value="config">
          <ConfigTab tipos={tipos} editable={editable} />
        </TabsContent>
        {canCertificacionSII && (
          <TabsContent value="certificacion">
            <CertificacionSII editable={editable && !readOnly} onIrConfig={() => setTab("config")} />
          </TabsContent>
        )}
      </Tabs>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Emitir
// ---------------------------------------------------------------------------
function EmitirTab({ tipos, blockedReason, onEmitido }) {
  const [clientes, setClientes] = useState([]);
  const [cafs, setCafs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tipo_doc: "33",
    contacto_id: "",
    fecha_emision: todayISO(),
    forma_pago: "credito",
    observaciones: "",
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [ref, setRef] = useState({ tipo_doc: "33", folio: "", fecha: "", razon: "1" });

  useEffect(() => {
    api.get("/clientes").then(({ data }) => setClientes(data)).catch((e) => toast.error(extractError(e)));
    api.get("/facturador/caf").then(({ data }) => setCafs(data)).catch(() => {});
  }, []);

  const tipoCfg = useMemo(() => tipos.find((t) => t.codigo === form.tipo_doc), [tipos, form.tipo_doc]);
  const esNota = tipoCfg?.es_nota;
  const esBoleta = form.tipo_doc === "39" || form.tipo_doc === "41";
  const totales = useMemo(() => calcTotales(tipoCfg, items), [tipoCfg, items]);
  // null = aún no se sabe (o no se pudo consultar): no bloquear por CAF
  const sinCaf = cafs !== null && !cafs.some(
    (c) => String(c.tipo_dte) === form.tipo_doc && c.estado === "activo" && c.folios_disponibles > 0
  );
  const motivoBloqueo = blockedReason
    || (sinCaf ? "No hay folios (CAF) disponibles para este tipo de documento" : null);

  const setItem = (idx, patch) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const delItem = (idx) => setItems((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const submit = async (e) => {
    e.preventDefault();
    if (motivoBloqueo) return toast.info(motivoBloqueo);
    if (!form.contacto_id && !esBoleta) return toast.error("Seleccione un cliente");
    if (!items.some((it) => it.nombre.trim())) return toast.error("Agregue al menos un ítem");
    setSaving(true);
    try {
      const payload = {
        ...form,
        contacto_id: form.contacto_id || null,
        items: items
          .filter((it) => it.nombre.trim())
          .map((it) => ({
            nombre: it.nombre,
            cantidad: Number(it.cantidad) || 0,
            precio_unitario: Number(it.precio_unitario) || 0,
            descuento_pct: Number(it.descuento_pct) || 0,
            exento: !!it.exento,
          })),
        referencia: esNota ? ref : null,
      };
      const { data } = await api.post("/facturador/emitir", payload);
      toast.success(`${data.tipo_doc_nombre} N° ${data.folio} emitida`);
      setItems([{ ...EMPTY_ITEM }]);
      setForm((f) => ({ ...f, contacto_id: "", observaciones: "" }));
      onEmitido?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-3 gap-5" data-testid="form-emitir">
      <div className="lg:col-span-2 space-y-5">
        <div className={`${CARD} p-4 sm:p-5 space-y-4`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-caps block mb-1">Tipo de documento</label>
              <select
                className={inputCls}
                value={form.tipo_doc}
                data-testid="emitir-tipo-doc"
                onChange={(e) => setForm({ ...form, tipo_doc: e.target.value })}
              >
                {tipos.map((t) => (
                  <option key={t.codigo} value={t.codigo}>{t.codigo} — {t.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Cliente</label>
              <select
                className={inputCls}
                value={form.contacto_id}
                data-testid="emitir-cliente"
                onChange={(e) => setForm({ ...form, contacto_id: e.target.value })}
              >
                <option value="">{esBoleta ? "Consumidor final (66.666.666-6)" : "Seleccione…"}</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.razon_social} ({c.rut})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Fecha emisión</label>
              <input type="date" className={inputCls} value={form.fecha_emision}
                onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })} />
            </div>
            <div>
              <label className="label-caps block mb-1">Forma de pago</label>
              <select className={inputCls} value={form.forma_pago}
                onChange={(e) => setForm({ ...form, forma_pago: e.target.value })}>
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </div>
          </div>

          {esNota && (
            <div className="border border-warning/40 bg-warning-surface rounded-sm p-3 space-y-3">
              <div className="flex items-center gap-2 text-[13px] font-medium text-foreground">
                <FileSignature className="h-4 w-4" /> Documento de referencia (obligatorio para notas)
              </div>
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <label className="label-caps block mb-1">Tipo ref.</label>
                  <select className={inputCls} value={ref.tipo_doc}
                    onChange={(e) => setRef({ ...ref, tipo_doc: e.target.value })}>
                    {tipos.filter((t) => !t.es_nota).map((t) => (
                      <option key={t.codigo} value={t.codigo}>{t.codigo} — {t.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-1">Folio ref.</label>
                  <input className={inputCls} value={ref.folio}
                    onChange={(e) => setRef({ ...ref, folio: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps block mb-1">Fecha ref.</label>
                  <input type="date" className={inputCls} value={ref.fecha}
                    onChange={(e) => setRef({ ...ref, fecha: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps block mb-1">Motivo</label>
                  <select className={inputCls} value={ref.razon}
                    onChange={(e) => setRef({ ...ref, razon: e.target.value })}>
                    <option value="1">Anula documento</option>
                    <option value="2">Corrige texto</option>
                    <option value="3">Corrige montos</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Ítems */}
        <div className={`${CARD} p-4 sm:p-5`}>
          <div className="flex items-center justify-between mb-3">
            <div className="label-caps">Detalle</div>
            <button type="button" onClick={addItem} data-testid="add-item"
              className={BTN_GHOST}>
              <Plus className="h-3.5 w-3.5" /> Agregar ítem
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-2">Descripción</th>
                  <th className="py-1.5 px-2 w-20 text-right">Cant.</th>
                  <th className="py-1.5 px-2 w-28 text-right">P. unit.</th>
                  <th className="py-1.5 px-2 w-20 text-right">Desc.%</th>
                  <th className="py-1.5 px-2 w-16 text-center">Exento</th>
                  <th className="py-1.5 pl-2 w-28 text-right">Monto</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const monto = Math.round(
                    (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) *
                    (1 - (Number(it.descuento_pct) || 0) / 100)
                  );
                  return (
                    <tr key={idx} className="border-t border-border">
                      <td className="py-1.5 pr-2">
                        <input className={inputCls} value={it.nombre} placeholder="Producto o servicio"
                          data-testid={`item-nombre-${idx}`}
                          onChange={(e) => setItem(idx, { nombre: e.target.value })} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" min="0" step="any" className={`${inputCls} text-right`} value={it.cantidad}
                          onChange={(e) => setItem(idx, { cantidad: e.target.value })} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" min="0" step="any" className={`${inputCls} text-right`} value={it.precio_unitario}
                          onChange={(e) => setItem(idx, { precio_unitario: e.target.value })} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" min="0" max="100" step="any" className={`${inputCls} text-right`} value={it.descuento_pct}
                          onChange={(e) => setItem(idx, { descuento_pct: e.target.value })} />
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <input type="checkbox" checked={it.exento}
                          onChange={(e) => setItem(idx, { exento: e.target.checked })} />
                      </td>
                      <td className="py-1.5 pl-2 text-right font-mono">{fmtCLP(monto)}</td>
                      <td className="py-1.5 text-center">
                        <button type="button" onClick={() => delItem(idx)}
                          className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <label className="label-caps block mb-1">Observaciones</label>
            <textarea className={inputCls} rows={2} value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="lg:col-span-1">
        <div className={`${CARD} p-4 sm:p-5 sticky top-4 space-y-3`}>
          <div className="label-caps">Resumen</div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Neto</span>
            <span className="font-mono">{fmtCLP(totales.neto)}</span>
          </div>
          {totales.exento > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Exento</span>
              <span className="font-mono">{fmtCLP(totales.exento)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">IVA 19%</span>
            <span className="font-mono">{fmtCLP(totales.iva)}</span>
          </div>
          <div className="flex justify-between text-base font-semibold pt-2 border-t border-border">
            <span>Total</span>
            <span className="font-mono">{fmtCLP(totales.total)}</span>
          </div>
          <span className="block" title={motivoBloqueo || undefined}>
            <button type="submit" disabled={saving || !!motivoBloqueo} data-testid="btn-emitir"
              aria-describedby={motivoBloqueo ? "emitir-bloqueo" : undefined}
              className={`${BTN_PRIMARY} w-full mt-2 disabled:cursor-not-allowed`}>
              {saving ? "Emitiendo…" : "Emitir documento"}
            </button>
          </span>
          {motivoBloqueo && (
            <p id="emitir-bloqueo" data-testid="emitir-bloqueo"
              className={`flex items-start gap-1.5 text-[12px] leading-snug ${
                blockedReason ? "text-muted-foreground" : "text-warning"
              }`}>
              {blockedReason
                ? <Lock className="h-3.5 w-3.5 mt-px shrink-0" />
                : <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />}
              <span>
                {motivoBloqueo}.
                {!blockedReason && " Cárguelo en la pestaña Configuración."}
                {blockedReason && " Puede completar el formulario para ver el cálculo de neto, IVA y total."}
              </span>
            </p>
          )}
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            El folio se asigna automáticamente desde el CAF cargado. Luego se firma y envía al SII
            desde la pestaña Documentos.
          </p>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------
const ESTADO_LABEL = {
  emitido_local: "Emitido (local)",
  firmado: "Firmado",
  enviado: "Enviado al SII",
};

const COMERCIAL_CLIENTE = {
  0: ["Cliente: aceptado", "text-emerald-600"],
  1: ["Cliente: aceptado con discrepancias", "text-sky-600"],
  2: ["Cliente: rechazado", "text-destructive"],
};

function RespuestaCliente({ intercambio }) {
  if (!intercambio) return null;
  const { recepcion, comercial, recibo } = intercambio;
  const [label, cls] = comercial ? COMERCIAL_CLIENTE[comercial.estado] || [] : [];
  return (
    <div className="text-[10px] mt-0.5 space-y-0.5" data-testid="respuesta-cliente">
      {label && <div className={cls} title={comercial.glosa}>{label}</div>}
      {!label && recepcion && (
        <div className={recepcion.estado === 0 ? "text-muted-foreground" : "text-destructive"} title={recepcion.glosa}>
          {recepcion.estado === 0 ? "Cliente: recibido" : "Cliente: recepción con reparos"}
        </div>
      )}
      {recibo && <div className="text-muted-foreground">Mercaderías recibidas</div>}
    </div>
  );
}

function DocumentosTab() {
  const { canWrite } = usePermissions();
  const editable = canWrite("facturador");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/facturador/documentos");
      setDocs(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const docsPage = useClientPagination(docs, String(docs.length));

  const verPdf = async (id, cedible = false) => {
    try {
      const res = await api.get(`/facturador/documentos/${id}/pdf`, { responseType: "blob", params: cedible ? { cedible: true } : {} });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const descargarXml = async (id) => {
    try {
      const res = await api.get(`/facturador/documentos/${id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `DTE_${id}.xml`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const accion = async (id, path, okMsg) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/facturador/documentos/${id}/${path}`);
      toast.success(okMsg + (data.track_id ? ` (Track ID ${data.track_id})` : ""));
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusyId(null);
    }
  };

  const consultarEstado = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.get(`/facturador/documentos/${id}/estado-sii`);
      toast.success(`SII: ${data.estado || "—"} ${data.glosa || ""}`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Folio</th>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th className="text-right">Total</th>
              <th>Estado DTE</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Cargando…</td></tr>
            )}
            {!loading && docs.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                Aún no hay documentos emitidos.
              </td></tr>
            )}
            {docsPage.pageItems.map((d) => {
              const busy = busyId === d.id;
              return (
                <tr key={d.id} data-testid={`doc-${d.id}`}>
                  <td className="font-mono">{d.folio}</td>
                  <td>{d.tipo_doc_nombre}</td>
                  <td>{fmtDate(d.fecha_emision)}</td>
                  <td className="max-w-[180px] truncate">{d.contacto_nombre}</td>
                  <td className="text-right font-mono font-medium">{fmtCLP(d.total)}</td>
                  <td>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] bg-muted text-muted-foreground">
                      {ESTADO_LABEL[d.dte_estado] || d.dte_estado || "—"}
                    </span>
                    {d.dte_estado_sii && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">SII: {d.dte_estado_sii}</div>
                    )}
                    <RespuestaCliente intercambio={d.intercambio} />
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 justify-end flex-wrap">
                      <button onClick={() => verPdf(d.id)} className={BTN_GHOST} title="Ver PDF">
                        <Download className="h-3.5 w-3.5" /> PDF
                      </button>
                      {d.dte_estado !== "emitido_local" && ["33", "34", "46", "52"].includes(String(d.tipo_doc)) && (
                        <button onClick={() => verPdf(d.id, true)} className={BTN_GHOST} title="Ejemplar cedible (con acuse de recibo)">
                          <Download className="h-3.5 w-3.5" /> Cedible
                        </button>
                      )}
                      {editable && d.dte_estado === "emitido_local" && (
                        <button onClick={() => accion(d.id, "firmar", "Documento firmado")} disabled={busy}
                          className={BTN_GHOST} title="Firmar electrónicamente">
                          <PenLine className="h-3.5 w-3.5" /> Firmar
                        </button>
                      )}
                      {editable && d.dte_estado === "firmado" && (
                        <>
                          <button onClick={() => accion(d.id, "enviar", "Enviado al SII")} disabled={busy}
                            className={BTN_GHOST} title="Enviar al SII">
                            <Send className="h-3.5 w-3.5" /> Enviar
                          </button>
                          <button onClick={() => descargarXml(d.id)} className={BTN_GHOST} title="Descargar XML">
                            <FileCode className="h-3.5 w-3.5" /> XML
                          </button>
                        </>
                      )}
                      {editable && d.dte_estado === "enviado" && (
                        <>
                          <button onClick={() => consultarEstado(d.id)} disabled={busy}
                            className={BTN_GHOST} title="Consultar estado en SII">
                            <RefreshCw className="h-3.5 w-3.5" /> Estado
                          </button>
                          <button onClick={() => descargarXml(d.id)} className={BTN_GHOST} title="Descargar XML">
                            <FileCode className="h-3.5 w-3.5" /> XML
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && docs.length > 0 && (
        <LibroPagination
          pagination={docsPage.pagination}
          pageSize={docsPage.pageSize}
          onPageSizeChange={docsPage.setPageSize}
          onPageChange={docsPage.setPage}
          testidPrefix="facturador-docs"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Configuración (emisor + CAF)
// ---------------------------------------------------------------------------
function ConfigTab({ tipos, editable }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <EmisorCard editable={editable} />
      <CafCard tipos={tipos} editable={editable} />
      <CertCard editable={editable} />
    </div>
  );
}

function CertCard({ editable }) {
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/facturador/certificado").then(({ data }) => setInfo(data)).catch((e) => toast.error(extractError(e)));
  useEffect(() => { load(); }, []);

  const subir = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleccione el archivo .pfx");
    if (!password) return toast.error("Ingrese la contraseña del certificado");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", password);
      await api.post("/facturador/certificado", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificado cargado");
      setPassword(""); setFile(null);
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${CARD} p-4 sm:p-5 space-y-3`} data-testid="cert-card">
      <div className="label-caps flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Certificado digital</div>
      {info?.configurado ? (
        <div className="text-[13px] space-y-1">
          <div className="flex items-center gap-2 text-success"><ShieldCheck className="h-4 w-4" /> Certificado cargado</div>
          <div className="text-muted-foreground">Titular: {info.titular || "—"}</div>
          <div className="text-muted-foreground font-mono">RUT: {info.rut || "—"}</div>
          <div className="text-muted-foreground">Válido hasta: {fmtDate(info.valido_hasta)}</div>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">No hay certificado cargado. Es necesario para firmar y enviar al SII.</p>
      )}
      {editable && (
        <form onSubmit={subir} className="space-y-2 pt-1 border-t border-border">
          <div>
            <label className="label-caps block mb-1 mt-2">Archivo .pfx / .p12</label>
            <input type="file" accept=".pfx,.p12" className="text-sm"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <label className="label-caps block mb-1">Contraseña del certificado</label>
            <input type="password" className={inputCls} value={password}
              onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" disabled={saving} className={`${BTN_PRIMARY} w-full`}>
            {saving ? "Cargando…" : info?.configurado ? "Reemplazar certificado" : "Cargar certificado"}
          </button>
        </form>
      )}
    </div>
  );
}

function EmisorCard({ editable }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/facturador/emisor").then(({ data }) => setForm(data)).catch((e) => toast.error(extractError(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/facturador/emisor", {
        actividad_economica: form.actividad_economica || "",
        codigo_actividad: form.codigo_actividad || "",
        comuna: form.comuna || "",
        ciudad: form.ciudad || "",
        direccion: form.direccion || "",
        telefono: form.telefono || "",
        email: form.email || "",
        codigo_sucursal: form.codigo_sucursal || "",
        unidad_sii: form.unidad_sii || "",
        sii_resolucion_numero: form.sii_resolucion_numero || "",
        sii_resolucion_fecha: form.sii_resolucion_fecha || "",
        sii_ambiente: form.sii_ambiente || "certificacion",
      });
      toast.success("Datos del emisor guardados");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className={`${CARD} p-5 text-sm text-muted-foreground`}>Cargando…</div>;

  return (
    <form onSubmit={save} className={`${CARD} p-4 sm:p-5 space-y-3`} data-testid="form-emisor">
      <div className="label-caps">Datos del emisor (SII)</div>
      <div className="text-[13px] text-muted-foreground">
        {form.nombre} — <span className="font-mono">{form.rut}</span>
      </div>
      <div>
        <label className="label-caps block mb-1">Actividad económica (giro)</label>
        <input className={inputCls} value={form.actividad_economica || ""}
          onChange={(e) => setForm({ ...form, actividad_economica: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Código actividad</label>
          <input className={inputCls} value={form.codigo_actividad || ""}
            onChange={(e) => setForm({ ...form, codigo_actividad: e.target.value })} />
        </div>
        <div>
          <label className="label-caps block mb-1">Ambiente SII</label>
          <select className={inputCls} value={form.sii_ambiente || "certificacion"}
            onChange={(e) => setForm({ ...form, sii_ambiente: e.target.value })}>
            <option value="certificacion">Certificación</option>
            <option value="produccion">Producción</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-caps block mb-1">Dirección</label>
        <input className={inputCls} value={form.direccion || ""}
          onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Comuna</label>
          <input className={inputCls} value={form.comuna || ""}
            onChange={(e) => setForm({ ...form, comuna: e.target.value })} />
        </div>
        <div>
          <label className="label-caps block mb-1">Ciudad</label>
          <input className={inputCls} value={form.ciudad || ""}
            onChange={(e) => setForm({ ...form, ciudad: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Teléfono</label>
          <input className={inputCls} value={form.telefono || ""}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
        </div>
        <div>
          <label className="label-caps block mb-1">Email</label>
          <input type="email" className={inputCls} value={form.email || ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Código sucursal SII</label>
          <input className={inputCls} value={form.codigo_sucursal || ""} placeholder="Opcional"
            onChange={(e) => setForm({ ...form, codigo_sucursal: e.target.value })} />
        </div>
        <div>
          <label className="label-caps block mb-1">Unidad SII</label>
          <input className={inputCls} value={form.unidad_sii || ""} placeholder="Ej: SANTIAGO CENTRO"
            onChange={(e) => setForm({ ...form, unidad_sii: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">N° Resolución SII</label>
          <input className={inputCls} value={form.sii_resolucion_numero || ""}
            onChange={(e) => setForm({ ...form, sii_resolucion_numero: e.target.value })} />
        </div>
        <div>
          <label className="label-caps block mb-1">Fecha Resolución</label>
          <input type="date" className={inputCls} value={form.sii_resolucion_fecha || ""}
            onChange={(e) => setForm({ ...form, sii_resolucion_fecha: e.target.value })} />
        </div>
      </div>
      {editable && (
        <button type="submit" disabled={saving} className={`${BTN_PRIMARY} w-full mt-1`}>
          {saving ? "Guardando…" : "Guardar emisor"}
        </button>
      )}
    </form>
  );
}

function CafCard({ tipos, editable }) {
  const [cafs, setCafs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const cafPage = useClientPagination(cafs, String(cafs.length), 10);

  const load = () =>
    api.get("/facturador/caf").then(({ data }) => setCafs(data)).catch((e) => toast.error(extractError(e)));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facturador/caf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`CAF cargado: tipo ${data.tipo_dte}, folios ${data.folio_desde}–${data.folio_hasta}`);
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/facturador/caf/${delTarget.id}`);
      toast.success("CAF eliminado");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const nombreTipo = (cod) => tipos.find((t) => String(t.codigo) === String(cod))?.nombre || cod;

  const resumen = useMemo(() => {
    const map = {};
    for (const c of cafs) {
      const key = `${c.tipo_dte}|${c.certificacion ? "cert" : "prod"}`;
      const row = map[key] || {
        tipo_dte: c.tipo_dte,
        certificacion: !!c.certificacion,
        disponibles: 0,
        usados: 0,
        caf_activos: 0,
        caf_total: 0,
      };
      row.caf_total += 1;
      row.usados += Number(c.folios_usados) || 0;
      if (c.estado === "activo") {
        row.caf_activos += 1;
        row.disponibles += Number(c.folios_disponibles) || 0;
      }
      map[key] = row;
    }
    return Object.values(map).sort((a, b) => {
      if (a.certificacion !== b.certificacion) return a.certificacion ? 1 : -1;
      return Number(a.tipo_dte) - Number(b.tipo_dte);
    });
  }, [cafs]);

  return (
    <div className={`${CARD} p-4 sm:p-5 space-y-3`} data-testid="caf-card">
      <div className="flex items-center justify-between">
        <div className="label-caps">Folios (CAF)</div>
        {editable && (
          <label className={`${BTN_GHOST} cursor-pointer`}>
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Cargando…" : "Cargar CAF"}
            <input type="file" accept=".xml" className="hidden" onChange={onUpload} disabled={uploading} />
          </label>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Suba el archivo XML del CAF descargado desde el SII (Solicitar Timbraje → Obtención de Folios).
        Cada CAF habilita un rango de folios para un tipo de documento.
      </p>

      {cafs.length === 0 ? (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground py-4">
          <AlertTriangle className="h-4 w-4 text-warning" /> No hay folios cargados todavía.
        </div>
      ) : (
        <>
          <div className="border border-border rounded-sm p-3" data-testid="caf-resumen">
            <div className="label-caps mb-1.5">Resumen por tipo</div>
            <table className="w-full text-[12px]">
              <tbody>
                {resumen.map((r) => (
                  <tr key={`${r.tipo_dte}-${r.certificacion ? "c" : "p"}`} className="border-t border-border/60 first:border-0">
                    <td className="py-1 pr-2">
                      {r.tipo_dte} · {nombreTipo(r.tipo_dte)}
                      {r.certificacion && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-warning/10 text-warning">Certificación</span>
                      )}
                    </td>
                    <td className={`text-right font-mono whitespace-nowrap ${r.disponibles > 0 ? "text-success" : "text-warning"}`}>
                      {r.disponibles} disp.
                      <span className="text-muted-foreground font-sans"> · {r.usados} usados</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">
              Disponibles sumando todos los CAF activos del tipo. El detalle de cada archivo está abajo.
            </p>
          </div>

          <div className="border border-border rounded-sm overflow-hidden" data-testid="caf-lista">
            <div className="label-caps px-3 pt-3 pb-1">Detalle de CAF cargados</div>
            <div className="divide-y divide-border">
              {cafPage.pageItems.map((c) => (
                <div key={c.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium flex items-center gap-2 flex-wrap">
                      {c.tipo_dte} — {nombreTipo(c.tipo_dte)}
                      {c.certificacion && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-warning/10 text-warning font-normal">Certificación</span>
                      )}
                    </div>
                    <div className="text-[12px] text-muted-foreground font-mono">
                      Folios {c.folio_desde}–{c.folio_hasta} · Disponibles: {c.folios_disponibles} · Usados: {c.folios_usados}
                    </div>
                    {c.antiguo && (
                      <div className="text-[11px] text-warning flex items-center gap-1 mt-0.5">
                        <AlertTriangle className="h-3 w-3" /> CAF con más de 2 años: el SII puede reparar/rechazar
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] px-2 py-0.5 rounded-sm ${
                      c.estado === "activo" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                    }`}>
                      {c.estado === "activo" ? "Activo" : "Agotado"}
                    </span>
                    {editable && c.folios_usados === 0 && (
                      <button onClick={() => setDelTarget(c)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <LibroPagination
              pagination={cafPage.pagination}
              pageSize={cafPage.pageSize}
              onPageSizeChange={cafPage.setPageSize}
              onPageChange={cafPage.setPage}
              testidPrefix="caf"
            />
          </div>
        </>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar CAF</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar el CAF de folios {delTarget?.folio_desde}–{delTarget?.folio_hasta}? Solo se permite si no tiene folios usados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
